import { useState, useEffect, useCallback } from 'react';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { AdminPaymentProcessor, AdminAnnualLicenseConfig } from '@/mocks/adminPaymentProcessors';
import {
  adminPaymentProcessors as mockProcessors,
  adminDefaultAnnualLicenseConfig,
} from '@/mocks/adminPaymentProcessors';

type LoadingState = 'loading' | 'ready' | 'error';

function simulateLoad(): Promise<AdminPaymentProcessor[]> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (Math.random() > 0.96) reject(new Error('Timeout al conectar.'));
      else resolve([...mockProcessors].sort((a, b) => a.order - b.order));
    }, 600);
  });
}

export default function SettingsPaymentsTab() {
  const [processors, setProcessors] = useState<AdminPaymentProcessor[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [licenseConfig] = useState<AdminAnnualLicenseConfig>(adminDefaultAnnualLicenseConfig);

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad().then((d) => { setProcessors(d); setLoadingState('ready'); }).catch((err) => { setErrorMsg(err.message || 'Error desconocido'); setLoadingState('error'); });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleTestConnection = (proc: AdminPaymentProcessor) => {
    if (proc.category === 'manual') { SwalAlert.warning('Método manual', 'Los métodos manuales no requieren prueba de conexión.'); return; }
    if (!proc.enabled) { SwalAlert.warning('Procesador inactivo', 'Este procesador no está habilitado.'); return; }
    setTestingId(proc.id);
    setTimeout(() => {
      const success = Math.random() > 0.3;
      setTestingId(null);
      if (success) {
        setProcessors((prev) => prev.map((p) => p.id === proc.id ? { ...p, lastTest: new Date().toISOString().slice(0, 16).replace('T', ' ') } : p));
        SwalAlert.toast(`${proc.name} responde correctamente`, 'success');
      } else {
        SwalAlert.error('Error de conexión', `${proc.name} no respondió.`);
      }
    }, 2000);
  };

  const activeCount = processors.filter((p) => p.enabled).length;

  return (
    <div className="space-y-5">
      {/* Annual License Card */}
      {loadingState === 'ready' && (
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0"><i className="ri-award-line text-emerald-600 text-lg"></i></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1"><h3 className="text-foreground-900 font-bold text-sm">{licenseConfig.title}</h3><span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold whitespace-nowrap">{licenseConfig.badge}</span></div>
              <p className="text-foreground-500 text-[10px] leading-relaxed">{licenseConfig.description}</p>
              <div className="flex items-center gap-3 mt-3"><span className="text-foreground-900 text-lg font-black">${licenseConfig.price}</span><span className="text-foreground-400 text-[10px]">{licenseConfig.priceLabel}</span></div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {licenseConfig.features.map((f, i) => (<span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[9px] font-medium"><i className="ri-check-line text-emerald-500 text-[9px]"></i>{f}</span>))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loadingState === 'loading' && (
        <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden animate-pulse p-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (<div key={i} className="flex items-center gap-4 py-2"><div className="w-8 h-8 rounded-lg bg-background-200"></div><div className="flex-1 space-y-1.5"><div className="w-24 h-3 bg-background-200 rounded"></div><div className="w-16 h-2 bg-background-200 rounded"></div></div></div>))}
        </div>
      )}

      {/* Error */}
      {loadingState === 'error' && (
        <div className="bg-background-50 border border-background-200 rounded-2xl p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-red-50 flex items-center justify-center"><i className="ri-error-warning-line text-red-400 text-xl"></i></div>
          <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar</p>
          <p className="text-foreground-400 text-xs mb-4">{errorMsg}</p>
          <button onClick={loadData} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"><i className="ri-refresh-line text-sm"></i>Reintentar</button>
        </div>
      )}

      {/* Table */}
      {loadingState === 'ready' && (
        <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50">
                  <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Procesador</th>
                  <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Estado</th>
                  <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Comisión</th>
                  <th className="text-right px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {processors.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-12 text-center"><p className="text-foreground-500 text-sm font-semibold">Sin métodos de pago</p></td></tr>
                )}
                {processors.map((proc) => {
                  const commissionDisplay = proc.commissionPercent > 0 || proc.commissionFixed > 0 ? `${proc.commissionPercent}% + $${proc.commissionFixed.toFixed(2)}` : 'Sin comisión';
                  return (
                    <tr key={proc.id} className={`hover:bg-background-100/60 transition-colors ${!proc.enabled ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${proc.logoColor}`}><i className={`${proc.icon} text-sm`}></i></div>
                          <div className="min-w-0"><p className="text-foreground-800 text-xs font-semibold truncate">{proc.name}</p></div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold ${proc.enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-background-200 text-foreground-400'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${proc.enabled ? 'bg-emerald-500' : 'bg-foreground-300'}`}></span>
                          {proc.enabled ? 'Disponible' : 'No disponible'}
                        </span>
                      </td>
                      <td className="px-4 py-3"><span className="text-foreground-800 text-xs font-mono font-semibold">{commissionDisplay}</span></td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => handleTestConnection(proc)} disabled={testingId === proc.id || !proc.enabled || proc.category === 'manual'}
                          className={`w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer transition-colors ml-auto ${!proc.enabled || proc.category === 'manual' ? 'bg-background-100 text-foreground-300 cursor-default' : 'bg-sky-50 text-sky-500 hover:bg-sky-100'}`}>
                          {testingId === proc.id ? <i className="ri-loader-4-line animate-spin text-xs"></i> : <i className="ri-plug-line text-xs"></i>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}